/**
 * Wires the "Enter fullscreen" / "Exit fullscreen" buttons rendered
 * inside a media-content video's .video-inline-wrapper, and traps
 * keyboard focus to just the iframe/video and the "Exit fullscreen"
 * button while the wrapper is actually in fullscreen.
 *
 * The video's own native fullscreen control (the iframe's internal
 * button, or the <video> element's built-in one) is disabled in block.php,
 * because fullscreening the iframe/video directly only ever shows that
 * single element — nothing else, including our own "Exit fullscreen"
 * button, can render alongside it. Fullscreening the *wrapper* instead
 * keeps both the player and our button in the same fullscreen element,
 * and lets us manage focus between them.
 */
class MediaContentVideoFullscreen {
	constructor(wrapperEl) {
		this.wrapperEl = wrapperEl;
		this.mediaEl = wrapperEl.querySelector('iframe, video');
		this.enterButton = wrapperEl.querySelector('[data-video-fullscreen-enter]');
		this.backButton = wrapperEl.querySelector('[data-video-fullscreen-back]');
		this.previouslyFocusedEl = null;

		if (this.enterButton) {
			this.enterButton.addEventListener('click', this.enterFullscreen);
		}

		if (this.backButton) {
			this.backButton.addEventListener('click', this.exitFullscreen);
		}

		document.addEventListener('fullscreenchange', this.onFullscreenChange);
		document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
	}

	isFullscreen = () => {
		const fullscreenEl = document.fullscreenElement || document.webkitFullscreenElement;

		return fullscreenEl === this.wrapperEl;
	};

	// The only two things focus should ever land on while in fullscreen:
	// the player itself and the way out. The "Enter fullscreen" button is
	// excluded on purpose — it's display:none while in fullscreen (see
	// media-content.scss), so it shouldn't be part of the tab order either.
	getFocusableEls = () => {
		return [this.mediaEl, this.backButton].filter(Boolean);
	};

	onFullscreenChange = () => {
		if (this.isFullscreen()) {
			this.previouslyFocusedEl = this.enterButton;

			window.addEventListener('keydown', this.focusTrapListener);
			window.addEventListener('blur', this.windowBlurListener);

			this.mediaEl?.focus();
		} else if (!this.isFullscreen() && this.previouslyFocusedEl) {
			// Only react to *this* wrapper's fullscreen session ending —
			// fullscreenchange fires for every instance on the page.
			window.removeEventListener('keydown', this.focusTrapListener);
			window.removeEventListener('blur', this.windowBlurListener);

			this.previouslyFocusedEl.focus();
			this.previouslyFocusedEl = null;
		}
	};

	/**
	 * Keep Tab/Shift+Tab cycling between the media element and the
	 * "Exit fullscreen" button instead of moving focus out to the page
	 * (or, once we're past the last real fullscreen instance, out of
	 * the tab entirely).
	 */
	focusTrapListener = (e) => {
		if (e.key !== 'Tab') return;

		const focusableEls = this.getFocusableEls();

		if (!focusableEls.length) return;

		const first = focusableEls[0];
		const last = focusableEls[focusableEls.length - 1];

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
			return;
		}

		if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
			return;
		}

		if (!focusableEls.includes(document.activeElement)) {
			e.preventDefault();
			first.focus();
		}
	};

	/**
	 * Recover focus when it leaves the browser tab entirely instead of
	 * landing on the "Exit fullscreen" button.
	 *
	 * This covers the case focusTrapListener can't: Tab-ing past the
	 * last control *inside* a cross-origin <iframe> (e.g. a YouTube/
	 * Vimeo player). That handoff is native browser behavior we never
	 * see a keydown for, and which element ends up focused next is
	 * inconsistent across browsers — sometimes it's the next DOM
	 * element (the button, which focusTrapListener would then manage
	 * normally), sometimes it's the browser's own UI, skipping the
	 * page entirely. `window` fires `blur` in both the "focus moved
	 * into the iframe" case and the "focus left the tab" case, so we
	 * distinguish them: while focus is legitimately inside our iframe,
	 * `document.activeElement` in this document *is* that <iframe>
	 * element. If it's not, and the document as a whole no longer has
	 * focus, focus really did leave the tab — pull it back.
	 */
	windowBlurListener = () => {
		setTimeout(() => {
			if (!this.isFullscreen()) return;

			const activeEl = document.activeElement;
			const focusedOurMedia = activeEl === this.mediaEl;

			if (focusedOurMedia || document.hasFocus()) {
				return;
			}

			(this.backButton || this.mediaEl)?.focus();
		}, 0);
	};

	enterFullscreen = () => {
		if (this.wrapperEl.requestFullscreen) {
			this.wrapperEl.requestFullscreen();
		} else if (this.wrapperEl.webkitRequestFullscreen) {
			// Safari
			this.wrapperEl.webkitRequestFullscreen();
		}
	};

	exitFullscreen = () => {
		if (document.exitFullscreen) {
			document.exitFullscreen();
		} else if (document.webkitExitFullscreen) {
			// Safari
			document.webkitExitFullscreen();
		}
	};
}

export default MediaContentVideoFullscreen;
